import { useState, useEffect, useRef } from "react";
import { C } from "../utils/tokens";
import { Tag } from "../components/primitives";
import { renderCV } from "../utils/renderCV";

const safeR = (v) => Math.max(0.001, isNaN(v) ? 0.001 : Math.abs(v));
const lerp = (a, b, t) => a + (b - a) * t;

// ══════════════════════════════════════════════════════════════════════════════
// LOCKED BG — griglia elettrica + fulmini + particelle intrappolate
// Sfondo a TUTTO SCHERMO, posizione fixed
// ══════════════════════════════════════════════════════════════════════════════
const LockedBg = () => {
  const ref = useRef();
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId,
      t = 0;
    const dpr = Math.min(window.devicePixelRatio, 2);
    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener("resize", resize);
    const W = () => window.innerWidth;
    const H = () => window.innerHeight;

    // Perlin noise
    const p = new Uint8Array(512);
    for (let i = 0; i < 256; i++) p[i] = i;
    for (let i = 255; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [p[i], p[j]] = [p[j], p[i]];
    }
    for (let i = 0; i < 256; i++) p[256 + i] = p[i];
    const fd = (t) => t * t * t * (t * (t * 6 - 15) + 10);
    const gd = (h, x, y) => {
      const v = h & 3,
        u = v < 2 ? x : y,
        w = v < 2 ? y : x;
      return (v & 1 ? -u : u) + (v & 2 ? -w : w);
    };
    const noise = (x, y) => {
      const X = Math.floor(x) & 255,
        Y = Math.floor(y) & 255;
      x -= Math.floor(x);
      y -= Math.floor(y);
      const u = fd(x),
        v = fd(y),
        a = p[X] + Y,
        b = p[X + 1] + Y;
      return lerp(
        lerp(gd(p[a], x, y), gd(p[b], x - 1, y), u),
        lerp(gd(p[a + 1], x, y - 1), gd(p[b + 1], x - 1, y - 1), u),
        v,
      );
    };

    // Trapped particles — bounce like they want to escape
    const N = 120;
    const pts = Array.from({ length: N }, () => ({
      x: 0.05 + Math.random() * 0.9,
      y: 0.05 + Math.random() * 0.9,
      vx: (Math.random() - 0.5) * 0.005,
      vy: (Math.random() - 0.5) * 0.005,
      size: 0.6 + Math.random() * 1.8,
      phase: Math.random() * Math.PI * 2,
    }));

    // Grid
    const COLS = 16,
      ROWS = 10;

    // Rings
    const rings = Array.from({ length: 6 }, (_, i) => ({
      r: 0.07 + i * 0.1,
      speed: (0.005 + i * 0.001) * (i % 2 ? 1 : -1),
      angle: Math.random() * Math.PI * 2,
      dash: 8 + i * 4,
      gap: 6 + i * 3,
    }));

    // Lightning
    const bolts = [];
    let nextBolt = 2;
    const makeBolt = (w, h) => {
      const cx = w * 0.5,
        cy = h * 0.5;
      const pts2 = [
        { x: cx + (Math.random() - 0.5) * w * 0.4, y: cy - h * 0.3 },
      ];
      while (pts2[pts2.length - 1].y < cy + h * 0.3) {
        const l = pts2[pts2.length - 1];
        pts2.push({
          x: l.x + (Math.random() - 0.5) * 50,
          y: l.y + 8 + Math.random() * 25,
        });
      }
      return { pts: pts2, life: 1 };
    };

    const draw = () => {
      const w = W(),
        h = H();
      if (w < 1 || h < 1) {
        animId = requestAnimationFrame(draw);
        return;
      }
      ctx.fillStyle = "rgba(8,9,9,0.18)";
      ctx.fillRect(0, 0, w, h);

      const cx = w * 0.5,
        cy = h * 0.5;
      const r = 245,
        g = 197,
        b2 = 24; // GOLD fixed

      // ── MORPHING GRID ──
      const gsx = w / COLS,
        gsy = h / ROWS;
      for (let gx = 0; gx <= COLS; gx++) {
        for (let gy = 0; gy <= ROWS; gy++) {
          const n = noise(gx * 0.25 + t * 0.07, gy * 0.25 + t * 0.055);
          const px = gx * gsx + n * 14,
            py = gy * gsy + n * 10;
          const a = 0.025 + Math.abs(n) * 0.05;
          ctx.fillStyle = `rgba(${r},${g},${b2},${a})`;
          ctx.beginPath();
          ctx.arc(px, py, safeR(0.8 + Math.abs(n) * 1.8), 0, Math.PI * 2);
          ctx.fill();
          if (gx < COLS) {
            const n2 = noise(
              (gx + 0.5) * 0.25 + t * 0.07,
              gy * 0.25 + t * 0.055,
            );
            ctx.strokeStyle = `rgba(${r},${g},${b2},${0.015 + Math.abs(n2) * 0.02})`;
            ctx.lineWidth = 0.4;
            ctx.beginPath();
            ctx.moveTo(px, py);
            ctx.lineTo((gx + 1) * gsx + n2 * 14, gy * gsy + n2 * 10);
            ctx.stroke();
          }
          if (gy < ROWS) {
            const n3 = noise(
              gx * 0.25 + t * 0.07,
              (gy + 0.5) * 0.25 + t * 0.055,
            );
            ctx.strokeStyle = `rgba(${r},${g},${b2},${0.015 + Math.abs(n3) * 0.02})`;
            ctx.lineWidth = 0.4;
            ctx.beginPath();
            ctx.moveTo(px, py);
            ctx.lineTo(gx * gsx + n3 * 14, (gy + 1) * gsy + n3 * 10);
            ctx.stroke();
          }
        }
      }

      // ── ORBITAL RINGS ──
      rings.forEach((ring, i) => {
        ring.angle += ring.speed;
        const rr = safeR(
          Math.min(w, h) * 0.38 * ring.r * (1 + Math.sin(t * 0.28 + i) * 0.04),
        );
        ctx.strokeStyle = `rgba(${r},${g},${b2},${0.035 + (6 - i) * 0.008})`;
        ctx.lineWidth = 0.5;
        ctx.setLineDash([ring.dash, ring.gap]);
        ctx.lineDashOffset = ring.angle * rr;
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(ring.angle);
        ctx.beginPath();
        ctx.ellipse(0, 0, rr, safeR(rr * 0.55), 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      });
      ctx.setLineDash([]);

      // ── TRAPPED PARTICLES ──
      pts.forEach((pt) => {
        pt.x += pt.vx;
        pt.y += pt.vy;
        if (pt.x < 0.04 || pt.x > 0.96) {
          pt.vx *= -1;
          pt.x = Math.max(0.04, Math.min(0.96, pt.x));
        }
        if (pt.y < 0.04 || pt.y > 0.96) {
          pt.vy *= -1;
          pt.y = Math.max(0.04, Math.min(0.96, pt.y));
        }
        // Weak pull to center
        pt.vx += (cx / w - pt.x) * 0.00006;
        pt.vy += (cy / h - pt.y) * 0.00006;
        pt.vx *= 0.998;
        pt.vy *= 0.998;
        const px = pt.x * w,
          py = pt.y * h;
        const pa = 0.15 + Math.sin(t * 0.8 + pt.phase) * 0.12;
        ctx.fillStyle = `rgba(${r},${g},${b2},${pa})`;
        ctx.beginPath();
        ctx.arc(px, py, safeR(pt.size), 0, Math.PI * 2);
        ctx.fill();
      });

      // ── LIGHTNING ──
      if (t > nextBolt) {
        bolts.push(makeBolt(w, h));
        nextBolt = t + 1.5 + Math.random() * 3;
      }
      for (let i = bolts.length - 1; i >= 0; i--) {
        const bolt = bolts[i];
        bolt.life -= 0.05;
        if (bolt.life <= 0) {
          bolts.splice(i, 1);
          continue;
        }
        ctx.strokeStyle = `rgba(${r},${g},${b2},${bolt.life * 0.5})`;
        ctx.lineWidth = bolt.life * 2;
        ctx.shadowColor = `rgba(${r},${g},${b2},${bolt.life * 0.7})`;
        ctx.shadowBlur = 14 * bolt.life;
        ctx.beginPath();
        bolt.pts.forEach((pt, i) =>
          i === 0 ? ctx.moveTo(pt.x, pt.y) : ctx.lineTo(pt.x, pt.y),
        );
        ctx.stroke();
        ctx.shadowBlur = 0;
      }

      // ── CENTRAL PULSE ──
      const pulse = 0.5 + Math.sin(t * 1.4) * 0.5;
      const cg = ctx.createRadialGradient(
        cx,
        cy,
        0,
        cx,
        cy,
        safeR(Math.min(w, h) * 0.2 * pulse),
      );
      cg.addColorStop(0, `rgba(${r},${g},${b2},${0.1 * pulse})`);
      cg.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = cg;
      ctx.fillRect(0, 0, w, h);

      // ── VIGNETTE ──
      const vg = ctx.createRadialGradient(
        cx,
        cy,
        0,
        cx,
        cy,
        safeR(Math.max(w, h) * 0.75),
      );
      vg.addColorStop(0.4, "rgba(0,0,0,0)");
      vg.addColorStop(1, "rgba(8,9,9,0.75)");
      ctx.fillStyle = vg;
      ctx.fillRect(0, 0, w, h);

      t += 0.016;
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);
  return (
    <canvas
      ref={ref}
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 0,
      }}
    />
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// UNLOCKED BG — spirale aurea + comete + burst + fireflies
// Sfondo a TUTTO SCHERMO, posizione fixed, completamente diverso
// ══════════════════════════════════════════════════════════════════════════════
const UnlockedBg = () => {
  const ref = useRef();
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId,
      t = 0;
    const dpr = Math.min(window.devicePixelRatio, 2);
    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener("resize", resize);
    const W = () => window.innerWidth;
    const H = () => window.innerHeight;

    // Golden spiral
    const PHI = 1.6180339887;
    const NSPIRAL = 160;
    const spiral = Array.from({ length: NSPIRAL }, (_, i) => ({
      angle: i * PHI * Math.PI * 2,
      r: 0.015 + i * 0.0055,
      speed: 0.006 + i * 0.00004,
      size: 0.5 + Math.random() * 2,
      phase: Math.random() * Math.PI * 2,
      col: i % 3, // 0=gold,1=green,2=white
    }));

    // Comets
    const NCOMETS = 18;
    const newComet = () => ({
      x: Math.random(),
      y: -Math.random() * 0.3,
      vx: (Math.random() - 0.5) * 0.0018,
      vy: 0.003 + Math.random() * 0.005,
      size: 0.7 + Math.random() * 1.6,
      len: 0.05 + Math.random() * 0.1,
      life: 0,
    });
    const comets = Array.from({ length: NCOMETS }, newComet);

    // Expanding burst rings
    const bursts = Array.from({ length: 5 }, (_, i) => ({
      r: i / 5,
      speed: 0.006 + i * 0.002,
      alpha: 1 - i / 5,
    }));

    // Aurora waves
    const NWAVES = 5;

    // Fireflies
    const NFLIES = 70;
    const flies = Array.from({ length: NFLIES }, () => ({
      x: Math.random(),
      y: Math.random(),
      vx: (Math.random() - 0.5) * 0.0025,
      vy: (Math.random() - 0.5) * 0.0025,
      size: 0.4 + Math.random() * 1.8,
      phase: Math.random() * Math.PI * 2,
    }));

    const draw = () => {
      const w = W(),
        h = H();
      if (w < 1 || h < 1) {
        animId = requestAnimationFrame(draw);
        return;
      }
      ctx.fillStyle = "rgba(8,9,9,0.07)";
      ctx.fillRect(0, 0, w, h);
      const cx = w * 0.5,
        cy = h * 0.42;

      // ── AURORA WAVES ──
      for (let i = 0; i < NWAVES; i++) {
        const wy =
          h * (0.15 + i * 0.17) + Math.sin(t * 0.22 + i * 1.1) * h * 0.07;
        const ww = safeR(w * (0.45 + Math.sin(t * 0.16 + i) * 0.12));
        const a = 0.025 + Math.sin(t * 0.38 + i) * 0.012;
        const wg = ctx.createRadialGradient(cx, wy, 0, cx, wy, ww * 0.5);
        if (i % 2 === 0) {
          wg.addColorStop(0, `rgba(76,175,125,${a * 4})`);
          wg.addColorStop(0.5, `rgba(76,175,125,${a})`);
          wg.addColorStop(1, "rgba(0,0,0,0)");
        } else {
          wg.addColorStop(0, `rgba(245,197,24,${a * 3})`);
          wg.addColorStop(0.5, `rgba(245,197,24,${a * 0.7})`);
          wg.addColorStop(1, "rgba(0,0,0,0)");
        }
        ctx.fillStyle = wg;
        ctx.beginPath();
        ctx.ellipse(
          cx,
          wy,
          ww * 0.5,
          safeR(h * 0.055),
          Math.sin(t * 0.06 + i) * 0.28,
          0,
          Math.PI * 2,
        );
        ctx.fill();
      }

      // ── BURST RINGS ──
      bursts.forEach((burst) => {
        burst.r = (burst.r + burst.speed) % 1.15;
        const br = safeR(Math.min(w, h) * 0.55 * burst.r);
        const ba = (1 - burst.r) * 0.12;
        if (ba < 0.005) return;
        ctx.strokeStyle = `rgba(76,175,125,${ba})`;
        ctx.lineWidth = 2 * (1 - burst.r);
        ctx.beginPath();
        ctx.arc(cx, cy, br, 0, Math.PI * 2);
        ctx.stroke();
        ctx.strokeStyle = `rgba(245,197,24,${ba * 0.55})`;
        ctx.lineWidth = 1 * (1 - burst.r);
        ctx.beginPath();
        ctx.arc(cx, cy, safeR(br * 0.82), 0, Math.PI * 2);
        ctx.stroke();
      });

      // ── GOLDEN SPIRAL ──
      const sBase = Math.min(w, h) * 0.35;
      spiral.forEach((pt) => {
        pt.angle += pt.speed;
        const d = safeR(
          sBase * pt.r * (1 + Math.sin(t * 0.38 + pt.phase) * 0.07),
        );
        const px = cx + Math.cos(pt.angle) * d,
          py = cy + Math.sin(pt.angle) * d * 0.72;
        const pa =
          (0.3 + Math.sin(t * 0.6 + pt.phase) * 0.4) * (0.5 + pt.r * 1.5);
        if (pa < 0.04) return;
        let sr = 245,
          sg2 = 197,
          sb = 24;
        if (pt.col === 1) {
          sr = 76;
          sg2 = 175;
          sb = 125;
        } else if (pt.col === 2) {
          sr = sg2 = sb = 255;
        }
        ctx.fillStyle = `rgba(${sr},${sg2},${sb},${pa})`;
        ctx.beginPath();
        ctx.arc(
          px,
          py,
          safeR(pt.size * (1 + Math.sin(t * 0.45 + pt.phase) * 0.28)),
          0,
          Math.PI * 2,
        );
        ctx.fill();
        if (pa > 0.55) {
          const gl = ctx.createRadialGradient(
            px,
            py,
            0,
            px,
            py,
            safeR(pt.size * 5),
          );
          gl.addColorStop(0, `rgba(${sr},${sg2},${sb},${pa * 0.28})`);
          gl.addColorStop(1, "rgba(0,0,0,0)");
          ctx.fillStyle = gl;
          ctx.beginPath();
          ctx.arc(px, py, safeR(pt.size * 5), 0, Math.PI * 2);
          ctx.fill();
        }
      });

      // ── COMETS ──
      comets.forEach((c, i) => {
        c.x += c.vx;
        c.y += c.vy;
        if (c.y > 1.1) {
          comets[i] = newComet();
          return;
        }
        c.life = Math.min(1, c.life + 0.008);
        const sx = c.x * w,
          sy = c.y * h;
        const tx2 = sx - (c.vx * w * c.len) / Math.abs(c.vx || 0.001);
        const ty2 = sy - (c.vy * h * c.len) / Math.abs(c.vy || 0.001);
        const sg = ctx.createLinearGradient(sx, sy, tx2, ty2);
        sg.addColorStop(0, `rgba(255,255,255,${c.life * 0.7})`);
        sg.addColorStop(0.35, `rgba(245,197,24,${c.life * 0.35})`);
        sg.addColorStop(1, "rgba(0,0,0,0)");
        ctx.strokeStyle = sg;
        ctx.lineWidth = c.size;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.lineTo(tx2, ty2);
        ctx.stroke();
        ctx.fillStyle = `rgba(255,255,255,${c.life * 0.85})`;
        ctx.beginPath();
        ctx.arc(sx, sy, safeR(c.size * 0.75), 0, Math.PI * 2);
        ctx.fill();
      });

      // ── FIREFLIES ──
      flies.forEach((fly) => {
        fly.x += fly.vx + Math.sin(t * 0.28 + fly.phase) * 0.0008;
        fly.y += fly.vy + Math.cos(t * 0.22 + fly.phase) * 0.0008;
        if (fly.x < 0) fly.x = 1;
        if (fly.x > 1) fly.x = 0;
        if (fly.y < 0) fly.y = 1;
        if (fly.y > 1) fly.y = 0;
        const pa = 0.12 + Math.sin(t * 1.1 + fly.phase) * 0.55;
        if (pa < 0.04) return;
        const col =
          fly.phase > Math.PI
            ? `rgba(245,197,24,${pa})`
            : `rgba(76,175,125,${pa})`;
        ctx.fillStyle = col;
        ctx.beginPath();
        ctx.arc(
          fly.x * w,
          fly.y * h,
          safeR(fly.size * (1 + Math.sin(t * 0.75 + fly.phase) * 0.35)),
          0,
          Math.PI * 2,
        );
        ctx.fill();
      });

      // ── MEGA CENTER GLOW ──
      const pulse = 0.55 + Math.sin(t * 1.8) * 0.45;
      const mg = ctx.createRadialGradient(
        cx,
        cy,
        0,
        cx,
        cy,
        safeR(Math.min(w, h) * 0.38 * pulse),
      );
      mg.addColorStop(0, `rgba(76,175,125,${0.2 * pulse})`);
      mg.addColorStop(0.45, `rgba(245,197,24,${0.07 * pulse})`);
      mg.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = mg;
      ctx.fillRect(0, 0, w, h);

      // ── SUCCESS CHECKMARK GLOW ──
      const cgl = ctx.createRadialGradient(cx, cy, 0, cx, cy, safeR(60));
      cgl.addColorStop(0, `rgba(76,175,125,${0.3 + Math.sin(t * 2.2) * 0.12})`);
      cgl.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = cgl;
      ctx.beginPath();
      ctx.arc(cx, cy, 60, 0, Math.PI * 2);
      ctx.fill();

      // ── VIGNETTE ──
      const vg = ctx.createRadialGradient(
        cx,
        cy,
        0,
        cx,
        cy,
        safeR(Math.max(w, h) * 0.8),
      );
      vg.addColorStop(0.35, "rgba(0,0,0,0)");
      vg.addColorStop(1, "rgba(8,9,9,0.65)");
      ctx.fillStyle = vg;
      ctx.fillRect(0, 0, w, h);

      t += 0.016;
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);
  return (
    <canvas
      ref={ref}
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 0,
      }}
    />
  );
};

// ── CONFETTI ──────────────────────────────────────────────────────────────────
const Confetti = ({ active }) => {
  if (!active) return null;
  return (
    <>
      <style>{`@keyframes cf{0%{transform:translateY(-10px) rotate(0deg);opacity:1}100%{transform:translateY(140px) rotate(900deg);opacity:0}}`}</style>
      {Array.from({ length: 28 }, (_, i) => (
        <div
          key={i}
          style={{
            position: "fixed",
            left: `${3 + Math.random() * 94}%`,
            top: `${10 + Math.random() * 20}%`,
            width: i % 4 === 0 ? 10 : 7,
            height: i % 4 === 0 ? 10 : 7,
            borderRadius: i % 3 === 0 ? "50%" : 2,
            background: [
              C.gold,
              "#4CAF7D",
              "#FF6B6B",
              "#60A5FA",
              "#E879F9",
              "#fff",
              "#FF9800",
            ][i % 7],
            animation: `cf ${0.6 + Math.random() * 1.4}s ${Math.random() * 0.6}s ease-in forwards`,
            zIndex: 999,
            pointerEvents: "none",
          }}
        />
      ))}
    </>
  );
};

// ── FEATURE ROW ───────────────────────────────────────────────────────────────
const FeatureRow = ({ text, delay = 0 }) => {
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setVis(true), delay);
    return () => clearTimeout(id);
  }, [delay]);
  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        alignItems: "center",
        padding: "8px 12px",
        background: "rgba(245,197,24,0.04)",
        border: "1px solid rgba(245,197,24,0.12)",
        borderRadius: 9,
        opacity: vis ? 1 : 0,
        transform: vis ? "translateX(0)" : "translateX(-12px)",
        transition: "all .4s ease",
      }}
    >
      <div
        style={{
          width: 20,
          height: 20,
          borderRadius: 6,
          flexShrink: 0,
          background: "rgba(245,197,24,0.15)",
          border: "1px solid rgba(245,197,24,0.3)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span style={{ color: C.gold, fontSize: 10 }}>✓</span>
      </div>
      <span style={{ fontSize: 12.5, color: C.text }}>{text}</span>
    </div>
  );
};

// ── MAIN ─────────────────────────────────────────────────────────────────────

const Step7 = ({ cvData, ai, cl, tpl, paid, setPaid }) => {
  const [loading, setL] = useState(false);
  const [confetti, setConfetti] = useState(false);
  const { info, 配置, esperienze, formazione, competenze } = cvData;
  const isEmpty = !info.nome && !info.cognome;

  useEffect(() => {
    if (paid) {
      setConfetti(true);
      setTimeout(() => setConfetti(false), 4000);
    }
  }, [paid]);

  const exportPDF = async () => {
    setL(true);
    try {
      if (!window.jspdf) {
        await new Promise((res, rej) => {
          const s = document.createElement("script");
          s.src =
            "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
          s.onload = res;
          s.onerror = rej;
          document.head.appendChild(s);
        });
      }
      const { jsPDF } = window.jspdf;
      const doc = new jsPDF({ unit: "mm", format: "a4" });
      const W = 210,
        M = 18,
        col = tpl.color;
      let y = 0;
      const hex2rgb = (h) => [
        parseInt(h.slice(1, 3), 16),
        parseInt(h.slice(3, 5), 16),
        parseInt(h.slice(5, 7), 16),
      ];
      const setCol = (h) => {
        const [r, g, b] = hex2rgb(h || "#333");
        doc.setTextColor(r, g, b);
      };
      const fillCol = (h) => {
        const [r, g, b] = hex2rgb(h || "#333");
        doc.setFillColor(r, g, b);
      };
      const drawCol = (h) => {
        const [r, g, b] = hex2rgb(h || "#333");
        doc.setDrawColor(r, g, b);
      };
      const sommario = ai?.sommarioAI || info.sommario || "";

      fillCol(col);
      doc.rect(0, 0, W, 42, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.setTextColor(255, 255, 255);
      doc.text(`${info.nome || ""} ${info.cognome || ""}`.trim(), M, 18);

      if (info.ruolo) {
        doc.setFontSize(11);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(255, 255, 255, 0.75);
        doc.text(info.ruolo, M, 26);
      }
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(220, 220, 220);
      const contacts = [info.email, info.telefono, info.citta, info.linkedin]
        .filter(Boolean)
        .join(" | ");
      if (contacts) doc.text(contacts, M, 35);

      y = 52;
      const secTitle = (label) => {
        drawCol(col);
        doc.setLineWidth(0.4);
        doc.line(M, y, M + 8, y);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        setCol(col);
        doc.setLetterSpacing(1.5);
        doc.text(label.toUpperCase(), M + 11, y + 0.8);
        doc.setLetterSpacing(0);
        drawCol("#ddd");
        doc.line(
          M + 11 + doc.getTextWidth(label.toUpperCase()) + 3,
          y,
          W - M,
          y,
        );
        y += 9;
      };

      const bodyText = (text, indent = 0, size = 10.5, color = "#333") => {
        setCol(color);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(size);
        const lines = doc.splitTextToSize(text, W - M * 2 - indent - 2);
        lines.forEach((l) => {
          if (y > 275) {
            doc.addPage();
            y = 18;
          }
          doc.text(l, M + indent, y);
          y += size * 0.45;
        });
        y += 2;
      };

      if (sommario) {
        secTitle("Profilo");
        bodyText(sommario, 0, 10, "#444");
        y += 2;
      }
      const exps = esperienze.filter((e) => e.azienda);
      if (exps.length) {
        secTitle("Esperienza");
        exps.forEach((e) => {
          if (y > 270) {
            doc.addPage();
            y = 18;
          }
          drawCol(col);
          doc.setLineWidth(1.2);
          doc.line(M, y - 3, M, y + 10);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(11.5);
          setCol("#111");
          doc.text(e.ruolo || "", M + 4, y);
          if (e.periodo) {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            setCol("#aaa");
            doc.text(e.periodo, W - M - doc.getTextWidth(e.periodo), y);
          }
          y += 5;
          if (e.azienda) {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            setCol(col);
            doc.text(e.azienda, M + 4, y);
            y += 5;
          }
          if (e.descrizione) bodyText(e.descrizione, 4, 9.5, "#555");
          y += 2;
        });
      }
      doc.save(`CV_${info.nome || "Nome"}_${info.cognome || "Cognome"}.pdf`);
    } catch (e) {
      console.error(e);
      alert("Errore PDF: " + e.message);
    }
    setL(false);
  };

  const handlePay = async (e) => {
    e.preventDefault();
    setL(true);
    try {
      const res = await fetch("/api/create-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Errore ${res.status}`);
      if (!data.url) throw new Error("URL pagamento non ricevuto");
      window.location.href = data.url;
    } catch (err) {
      console.error(err);
      alert(`Errore: ${err.message}`);
      setL(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes priceGlow{0%,100%{text-shadow:0 0 24px rgba(245,197,24,0.6)}50%{text-shadow:0 0 60px rgba(245,197,24,1),0 0 100px rgba(245,197,24,0.4)}}
        @keyframes btnFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-4px)}}
        @keyframes successRing{0%,100%{box-shadow:0 0 0 0 rgba(76,175,125,0.5)}60%{box-shadow:0 0 0 22px rgba(76,175,125,0)}}
        @keyframes successFloat{0%,100%{transform:translateY(0) scale(1)}50%{transform:translateY(-6px) scale(1.03)}}
        @keyframes scanLine{0%{top:-2px}100%{top:100%}}
        @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        
        /* ── CORREZIONE MOBILE ULTRA-ROBUSTA ── */
        .paywall-container {
          display: flex;
          flex-direction: row;
          gap: 28px;
          align-items: flex-start;
        }
        .price-table-box {
          display: flex;
          border-radius: 18px;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.08);
          margin-bottom: 22px;
          width: 100%;
        }
        .old-price-section {
          background: rgba(255,82,82,0.07);
          padding: 18px 22px;
          display: flex;
          flex-direction: column;
          align-items: center;
          border-right: 1px solid rgba(255,255,255,0.06);
          flex: 1;
        }
        .new-price-section {
          background: linear-gradient(135deg,rgba(245,197,24,0.12),rgba(245,197,24,0.04));
          padding: 18px 22px;
          display: flex;
          flex-direction: column;
          align-items: center;
          flex: 1;
        }
        .stripe-notes-grid {
          display: flex;
          justify-content: center;
          gap: 10px;
          margin-top: 12px;
          flex-wrap: wrap;
        }

        @media (max-width: 768px) {
          .cv-header-container {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 12px !important;
          }
          .paywall-content-wrapper {
            padding: 24px 16px !important;
          }
          .paywall-container {
            flex-direction: column !important;
            gap: 24px !important;
          }
          .left-pricing-block {
            width: 100% !important;
          }
          /* Trasforma i prezzi in griglia verticale per non tagliarli */
          .price-table-box {
            flex-direction: column !important;
          }
          .old-price-section {
            border-right: none !important;
            border-bottom: 1px solid rgba(255,255,255,0.06) !important;
            padding: 12px !important;
          }
          .new-price-section {
            padding: 16px !important;
          }
          /* Ridimensiona il testo del bottone per farlo stare in una riga su mobile */
          .cta-pay-btn {
            font-size: 14px !important;
            padding: 14px 16px !important;
          }
          .stripe-notes-grid {
            gap: 6px 12px !important;
          }
          .success-container {
            padding: 32px 16px !important;
          }
          .success-download-btn {
            width: 100% !important;
          }
        }
      `}</style>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 28,
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* ── HEADER ANTEPRIMA ── */}
        <div>
          <div
            className="cv-header-container"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <div>
              <h2
                style={{
                  fontFamily: "'Syne',sans-serif",
                  fontSize: 26,
                  fontWeight: 800,
                  marginBottom: 4,
                  color: "#fff",
                }}
              >
                Il tuo CV è pronto 🎉
              </h2>
              <p style={{ color: "#888", fontSize: 13.5 }}>
                Controlla l'anteprima, poi sblocca il download.
              </p>
            </div>
            <div
              style={{
                background: "rgba(245,197,24,0.15)",
                color: "#F5C518",
                border: "1px solid #F5C518",
                padding: "6px 14px",
                borderRadius: 100,
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {tpl.icon} {tpl.name}
            </div>
          </div>

          <div
            style={{
              position: "relative",
              borderRadius: 16,
              overflow: "hidden",
              boxShadow: `0 0 60px rgba(245,197,24,0.15)`,
            }}
          >
            {renderCV(tpl, info, esperienze, formazione, competenze, ai)}
            {!paid && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  pointerEvents: "none",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    height: 2,
                    background:
                      "linear-gradient(to right,transparent,rgba(245,197,24,0.5),transparent)",
                    animation: "scanLine 3s linear infinite",
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* ── PAYWALL COMPONENTE RIGENERATO ── */}
        {!paid ? (
          <div
            style={{
              position: "relative",
              borderRadius: 24,
              overflow: "hidden",
              border: "1px solid rgba(245,197,24,0.2)",
              background: "rgba(8,9,9,0.75)",
              backdropFilter: "blur(28px)",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 2,
                background:
                  "linear-gradient(to right,transparent,rgba(245,197,24,0.8),transparent)",
                boxShadow: "0 0 20px rgba(245,197,24,0.6)",
                zIndex: 3,
              }}
            />

            <div
              className="paywall-content-wrapper"
              style={{ position: "relative", zIndex: 2, padding: "36px 32px" }}
            >
              <div className="paywall-container">
                {/* BLOCCO SINISTRO: PREZZI & PAGAMENTO */}
                <div
                  className="left-pricing-block"
                  style={{ flex: "1 1 0%", width: "100%", maxWidth: "100%" }}
                >
                  {/* Badge sblocca */}
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "5px 14px",
                      background: "rgba(245,197,24,0.1)",
                      border: "1px solid rgba(245,197,24,0.3)",
                      borderRadius: 100,
                      marginBottom: 22,
                    }}
                  >
                    <span style={{ fontSize: 13 }}>🔒</span>
                    <span
                      style={{
                        fontSize: 10.5,
                        color: "#F5C518",
                        fontFamily: "'DM Mono',monospace",
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                      }}
                    >
                      Sblocca il download
                    </span>
                  </div>

                  {/* Tabella Prezzi Flessibile */}
                  <div className="price-table-box">
                    {/* Vecchio Prezzo */}
                    <div className="old-price-section">
                      <div
                        style={{
                          fontSize: 9,
                          color: "#ff5252",
                          fontFamily: "'DM Mono',monospace",
                          letterSpacing: "0.1em",
                          textTransform: "uppercase",
                          marginBottom: 5,
                          opacity: 0.8,
                        }}
                      >
                        Prezzo pieno
                      </div>
                      <div style={{ position: "relative" }}>
                        <span
                          style={{
                            fontSize: 36,
                            fontWeight: 900,
                            color: "rgba(255,82,82,0.45)",
                            fontFamily: "'Syne',sans-serif",
                            letterSpacing: "-0.04em",
                            lineHeight: 1,
                          }}
                        >
                          €4,99
                        </span>
                        <div
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: -3,
                            right: -3,
                            height: 2,
                            background: "#ff5252",
                            borderRadius: 2,
                            transform: "rotate(-6deg)",
                          }}
                        />
                      </div>
                    </div>

                    {/* Nuovo Prezzo Offerta */}
                    <div className="new-price-section">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 7,
                          marginBottom: 5,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 9,
                            color: "#4CAF7D",
                            fontFamily: "'DM Mono',monospace",
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                          }}
                        >
                          🎉 Lancio
                        </span>
                        <div
                          style={{
                            background: "#4CAF7D",
                            color: "#000",
                            fontSize: 8.5,
                            fontWeight: 900,
                            padding: "2px 7px",
                            borderRadius: 20,
                          }}
                        >
                          -40%
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: 44,
                          fontWeight: 900,
                          color: "#F5C518",
                          fontFamily: "'Syne',sans-serif",
                          letterSpacing: "-0.04em",
                          lineHeight: 1,
                          animation: "priceGlow 2.5s infinite",
                        }}
                      >
                        €2,99
                      </span>
                      <div
                        style={{ fontSize: 9.5, color: "#888", marginTop: 4 }}
                      >
                        una tantum · nessun abbonamento
                      </div>
                    </div>
                  </div>

                  {/* Pulsante di Pagamento flessibile */}
                  <button
                    onClick={handlePay}
                    disabled={loading}
                    className="cta-pay-btn"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 10,
                      width: "100%",
                      padding: "16px 24px",
                      background: loading
                        ? "rgba(255,255,255,0.06)"
                        : "linear-gradient(135deg,#F5C518,#D4940A)",
                      border: "none",
                      borderRadius: 14,
                      fontFamily: "'Syne',sans-serif",
                      fontWeight: 900,
                      fontSize: 15,
                      color: loading ? "#888" : "#0A0600",
                      cursor: loading ? "not-allowed" : "pointer",
                      animation: loading ? "none" : "btnFloat 3s ease infinite",
                      boxShadow: loading
                        ? "none"
                        : "0 8px 32px rgba(245,197,24,0.25)",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {loading ? (
                      <>
                        <div
                          style={{
                            width: 17,
                            height: 17,
                            border: "2px solid rgba(245,197,24,0.3)",
                            borderTopColor: "#F5C518",
                            borderRadius: "50%",
                            animation: "spin .7s linear infinite",
                          }}
                        />
                        Connessione sicura Stripe...
                      </>
                    ) : (
                      <>
                        <span>💳</span> Paga €2,99 e scarica ora
                      </>
                    )}
                  </button>

                  {/* Note di Sicurezza */}
                  <div className="stripe-notes-grid">
                    {[
                      "🔒 Gateway Stripe",
                      "Nessun rinnovo",
                      "Rimborso 100%",
                    ].map((l, i) => (
                      <span
                        key={i}
                        style={{
                          fontSize: 11,
                          color: "#888",
                          whiteSpace: "nowrap",
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        • {l}
                      </span>
                    ))}
                  </div>
                </div>

                {/* BLOCCO DESTRO: FUNZIONALITÀ COMPRESE */}
                <div
                  style={{
                    flex: "1 1 0%",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      color: "#F5C518",
                      fontFamily: "'DM Mono',monospace",
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      marginBottom: 4,
                      opacity: 0.8,
                    }}
                  >
                    Cosa include l'acquisto
                  </div>
                  {[
                    "File PDF ad alta risoluzione pronto all'uso",
                    "Ottimizzazione per i sistemi ATS aziendali",
                    "Accesso illimitato al download per sempre",
                    "Modifiche gratuite illimitate senza sovrapprezzo",
                    `Design esclusivo basato sul modello Neon Dark`,
                    "Inclusione automatica del sommario AI generato",
                  ].map((f, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        background: "rgba(255,255,255,0.02)",
                        border: "1px solid rgba(255,255,255,0.04)",
                        padding: "12px 14px",
                        borderRadius: 12,
                      }}
                    >
                      <span style={{ color: "#F5C518", fontSize: 13 }}>✓</span>
                      <span style={{ fontSize: 13, color: "#e0e0e0" }}>
                        {f}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* ── SUCCESS SCREEN ── */
          <div
            style={{
              position: "relative",
              borderRadius: 24,
              overflow: "hidden",
              border: "1px solid rgba(76,175,125,0.35)",
              background: "rgba(8,9,9,0.75)",
              backdropFilter: "blur(28px)",
            }}
          >
            <div
              className="success-container"
              style={{ padding: "48px 36px", textAlign: "center" }}
            >
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  margin: "0 auto 20px",
                  background: "rgba(76,175,125,0.15)",
                  border: "2px solid #4CAF7D",
                  display: "flex",
                  alignItems: "center",
                  justifyCenter: "center",
                  fontSize: 32,
                  color: "#4CAF7D",
                  animation: "successRing 2s infinite",
                }}
              >
                ✓
              </div>
              <h2
                style={{
                  fontFamily: "'Syne',sans-serif",
                  fontWeight: 900,
                  fontSize: 26,
                  color: "#fff",
                  marginBottom: 8,
                }}
              >
                Sbloccato con successo! 🎉
              </h2>
              <p style={{ color: "#888", fontSize: 13.5, marginBottom: 24 }}>
                Il tuo account ha ora l'accesso a vita su questo template.
              </p>

              {!isEmpty ? (
                <button
                  onClick={exportPDF}
                  disabled={loading}
                  className="success-download-btn"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 10,
                    padding: "15px 36px",
                    background: "linear-gradient(135deg,#F5C518,#D4940A)",
                    border: "none",
                    borderRadius: 14,
                    fontFamily: "'Syne',sans-serif",
                    fontWeight: 900,
                    fontSize: 15,
                    color: "#0A0600",
                    cursor: "pointer",
                    boxShadow: "0 8px 32px rgba(245,197,24,0.3)",
                  }}
                >
                  {loading ? "Generazione file..." : "📥 Scarica il tuo CV PDF"}
                </button>
              ) : (
                <div style={{ fontSize: 13, color: "#ff5252" }}>
                  Compila i campi principali per scaricare il documento.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Step7;
